import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApplicationQuestionsManager from './components/ApplicationQuestionsManager';
import FormCustomizationPanel from './components/FormCustomizationPanel';
import ScreeningAutomationPanel from './components/ScreeningAutomationPanel';
import GreetingMessageEditor from './components/GreetingMessageEditor';
import ThemeCustomizer from './components/ThemeCustomizer';
import { apiFetch } from './services/apiClient';
import { splitQuestionsForApplyPreview } from './utils/applyFormFieldOrder';
import './ApplicationFormSetupPage.css';

const ApplicationFormSetupPage = () => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1); // 1: Questions, 2: Settings, 3: Greeting, 4: Preview
    const [jobDetails, setJobDetails] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [formSettings, setFormSettings] = useState(null);
    const [greetingMessage, setGreetingMessage] = useState('');
    const [previewData, setPreviewData] = useState(null);
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        fetchJobDetails();
        fetchQuestions();
        fetchFormSettings();
        fetchGreeting();
    }, [jobId]);

    const fetchJobDetails = async () => {
        try {
            const response = await apiFetch(`/api/jobs/${jobId}`, {
            });
            if (response.ok) {
                const data = await response.json();
                setJobDetails(data);
            }
        } catch (error) {
            console.error('Error fetching job details:', error);
        }
    };

    const fetchQuestions = async () => {
        try {
            const response = await apiFetch(`/api/jobs/${jobId}/application-questions`, {
            });
            if (response.ok) {
                const data = await response.json();
                setQuestions(data);
            }
        } catch (error) {
            console.error('Error fetching questions:', error);
        }
    };

    const fetchFormSettings = async () => {
        try {
            const response = await apiFetch(`/api/jobs/${jobId}/form-settings`, {
            });
            if (response.ok) {
                const data = await response.json();
                setFormSettings(data);
            }
        } catch (error) {
            console.error('Error fetching form settings:', error);
        }
    };

    const fetchGreeting = async () => {
        try {
            const response = await apiFetch(`/api/jobs/${jobId}/greeting`, {
            });
            if (response.ok) {
                const data = await response.json();
                setGreetingMessage(data.greeting_message || '');
            }
        } catch (error) {
            console.error('Error fetching greeting:', error);
        }
    };

    const handleNext = () => {
        if (currentStep < 4) {
            if (currentStep === 3) {
                // Before going to preview, gather all data
                preparePreview();
            }
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const preparePreview = async () => {
        // Fetch latest data for preview
        const [questionsData, settingsData, greetingData] = await Promise.all([
            fetch(`/api/jobs/${jobId}/application-questions`, {  }).then(r => r.ok ? r.json() : []),
            fetch(`/api/jobs/${jobId}/form-settings`, {  }).then(r => r.ok ? r.json() : {}),
            fetch(`/api/jobs/${jobId}/greeting`, {  }).then(r => r.ok ? r.json() : { greeting_message: '' })
        ]);
        
        setQuestions(questionsData);
        setFormSettings(settingsData);
        setGreetingMessage(greetingData.greeting_message || '');
        
        setPreviewData({
            questions: questionsData,
            formSettings: settingsData,
            greetingMessage: greetingData.greeting_message || '',
            jobTitle: jobDetails?.description?.split('\n')[0] || 'Job Title',
            jobDescription: jobDetails?.description || ''
        });
    };

    const handleCreateForm = async () => {
        setIsCreating(true);
        try {
            // Generate application URL
            const response = await apiFetch(`/api/jobs/${jobId}/application-url`, {
                method: 'POST',
            });

            if (response.ok) {
                const data = await response.json();
                // Show success message with URL
                alert(`Application form created successfully!\n\nApplication URL: ${data.application_url}\n\nYou can copy this URL and use it when posting on LinkedIn.`);
                // Navigate back to jobs list
                navigate('/jobs');
            } else {
                const error = await response.json();
                alert(error.error || 'Failed to create application form');
            }
        } catch (error) {
            console.error('Error creating form:', error);
            alert('Failed to create application form');
        } finally {
            setIsCreating(false);
        }
    };

    const steps = [
        { number: 1, title: 'Questions', component: 'questions' },
        { number: 2, title: 'Form Settings', component: 'settings' },
        { number: 3, title: 'Greeting Message', component: 'greeting' },
        { number: 4, title: 'Preview & Create', component: 'preview' }
    ];

    return (
        <div className="application-form-setup-page">
            <ThemeCustomizer 
                jobId={jobId} 
                currentSettings={formSettings}
                onThemeChange={(newSettings) => {
                    setFormSettings(newSettings);
                    // Save immediately when changed
                    apiFetch(`/api/jobs/${jobId}/form-settings`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(newSettings)
                    }).catch(err => console.error('Error saving theme:', err));
                }}
            />
            <div className="setup-container">
                <div className="setup-header">
                    <h2>Setup Application Form</h2>
                    <p>Configure your LinkedIn application form step by step</p>
                </div>

                {/* Step Indicator */}
                <div className="step-indicator">
                    {steps.map((step, index) => (
                        <React.Fragment key={step.number}>
                            <div className={`step-item ${currentStep >= step.number ? 'active' : ''} ${currentStep === step.number ? 'current' : ''}`}>
                                <div className="step-number">{step.number}</div>
                                <div className="step-title">{step.title}</div>
                            </div>
                            {index < steps.length - 1 && <div className="step-connector" />}
                        </React.Fragment>
                    ))}
                </div>

                {/* Step Content */}
                <div className="step-content">
                    {currentStep === 1 && (
                        <div className="step-panel">
                            <h3>Step 1: Application Questions</h3>
                            <p className="step-description">Configure your application form. Resume upload is always required.</p>
                            
                            {/* Show Job Description First */}
                            <div className="job-description-preview" style={{
                                padding: '1.5rem',
                                background: 'rgba(255, 255, 255, 0.08)',
                                borderRadius: '8px',
                                marginBottom: '1.5rem',
                                border: '1px solid var(--glass-border-color)'
                            }}>
                                <h4 style={{ marginTop: 0, marginBottom: '1rem', color: 'rgba(255, 255, 255, 0.95)' }}>Job Description</h4>
                                <div style={{ 
                                    whiteSpace: 'pre-wrap', 
                                    color: 'rgba(255, 255, 255, 0.85)',
                                    lineHeight: '1.6',
                                    maxHeight: '300px',
                                    overflowY: 'auto'
                                }}>
                                    {jobDetails?.description || 'No job description'}
                                </div>
                            </div>

                            {/* Start your application — before resume (matches candidate apply / docx) */}
                            <div className="resume-field-section" style={{
                                padding: '1.5rem',
                                border: '1px solid var(--glass-border-color)',
                                borderRadius: '8px',
                                marginBottom: '1.5rem',
                                background: 'rgba(255, 255, 255, 0.08)'
                            }}>
                                <h4 style={{ marginTop: 0, marginBottom: '0.35rem', color: 'rgba(255, 255, 255, 0.95)' }}>
                                    Start your application
                                </h4>
                                <p style={{ marginTop: 0, color: 'rgba(255, 255, 255, 0.75)' }}>
                                    Candidates enter first name, last name, and email first.
                                </p>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                    <div>
                                        <label style={{ color: 'rgba(255, 255, 255, 0.9)' }}>First name *</label>
                                        <input type="text" disabled placeholder="First name" style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ddd' }} />
                                    </div>
                                    <div>
                                        <label style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Last name *</label>
                                        <input type="text" disabled placeholder="Last name" style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ddd' }} />
                                    </div>
                                </div>
                                <div>
                                    <label style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Email address *</label>
                                    <input type="email" disabled placeholder="Email" style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #ddd' }} />
                                </div>
                                <small style={{ display: 'block', marginTop: '0.75rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                                    Then phone number and city &amp; state (seeded below), then résumé upload.
                                </small>
                            </div>

                            {/* Additional Questions Section — phone/city/state appear first via seed order */}
                            <div className="additional-questions-section">
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    marginBottom: '1.5rem',
                                    paddingBottom: '1rem',
                                    borderBottom: '2px solid rgba(255, 255, 255, 0.2)'
                                }}>
                                    <div>
                                        <h4 style={{ margin: 0, color: 'rgba(255, 255, 255, 0.95)' }}>Application Questions</h4>
                                        <small style={{ color: 'rgba(255, 255, 255, 0.75)', fontSize: '0.9rem' }}>
                                            Phone, City &amp; State come before résumé on the candidate form. Add more questions if needed.
                                        </small>
                                    </div>
                                </div>

                                <ApplicationQuestionsManager 
                                    jobId={jobId} 
                                    jobTitle={jobDetails?.description?.split('\n')[0]}
                                    onQuestionsChange={setQuestions}
                                    showResumeField={true}
                                    resumeAfterPreResumeFields={true}
                                />
                            </div>
                        </div>
                    )}

                    {currentStep === 2 && (
                        <div className="step-panel">
                            <h3>Step 2: Form Settings</h3>
                            <p className="step-description">Customize the appearance, visibility, and AI screening automation for this job.</p>
                            <FormCustomizationPanel 
                                jobId={jobId}
                                onSettingsChange={setFormSettings}
                            />
                            <ScreeningAutomationPanel jobId={jobId} className="setup-embedded" />
                        </div>
                    )}

                    {currentStep === 3 && (
                        <div className="step-panel">
                            <h3>Step 3: Greeting Message</h3>
                            <p className="step-description">Set a custom greeting message for candidates (optional).</p>
                            <GreetingMessageEditor 
                                jobId={jobId}
                                onGreetingChange={setGreetingMessage}
                            />
                        </div>
                    )}

                    {currentStep === 4 && (
                        <div className="step-panel">
                            <h3>Step 4: Preview & Create</h3>
                            <p className="step-description">Review your application form and create it.</p>
                            
                            <div className="preview-container">
                                <div className="preview-form"                                     style={{
                                        backgroundColor: (previewData?.formSettings || formSettings)?.form_background_color || '#FFFFFF',
                                        color: (previewData?.formSettings || formSettings)?.form_text_color || '#000000',
                                        padding: '2rem',
                                        borderRadius: '8px',
                                        border: '1px solid #ddd'
                                    }}>
                                    {(previewData?.formSettings || formSettings)?.form_show_job_title && (
                                        <h2 style={{ marginTop: 0, color: (previewData?.formSettings || formSettings)?.form_text_color || '#000000' }}>
                                            {previewData?.jobTitle || jobDetails?.description?.split('\n')[0] || 'Job Title'}
                                        </h2>
                                    )}
                                    
                                    {(previewData?.formSettings || formSettings)?.form_show_job_description && (
                                        <div style={{ marginBottom: '1.5rem', color: (previewData?.formSettings || formSettings)?.form_text_color || '#000000' }}>
                                            <p>{previewData?.jobDescription || jobDetails?.description || 'Job Description'}</p>
                                        </div>
                                    )}

                                    {(previewData?.greetingMessage || greetingMessage) && (
                                        <div className="greeting-preview" style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(255, 255, 255, 0.95)', borderRadius: '4px', color: '#333' }}>
                                            <p style={{ margin: 0, lineHeight: 1.6, color: 'inherit' }}>{previewData?.greetingMessage || greetingMessage}</p>
                                        </div>
                                    )}

                                    <div className="form-preview">
                                        <div className="preview-field" style={{ marginBottom: '1rem', padding: '1rem', background: '#f8f8f8', borderRadius: 8 }}>
                                            <h3 style={{ marginTop: 0 }}>Start your application</h3>
                                            <p style={{ color: '#666', marginTop: 0 }}>Enter your name and email to continue.</p>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                                <div>
                                                    <label><strong>First name *</strong></label>
                                                    <input type="text" disabled placeholder="First name" style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', width: '100%' }} />
                                                </div>
                                                <div>
                                                    <label><strong>Last name *</strong></label>
                                                    <input type="text" disabled placeholder="Last name" style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', width: '100%' }} />
                                                </div>
                                            </div>
                                            <div style={{ marginTop: '0.75rem' }}>
                                                <label><strong>Email address *</strong></label>
                                                <input type="email" disabled placeholder="Email" style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', width: '100%' }} />
                                            </div>
                                        </div>

                                        {(() => {
                                            const allQs = previewData?.questions || questions;
                                            const { pre, post } = splitQuestionsForApplyPreview(allQs);
                                            const renderQ = (q, idx) => (
                                                <div key={q.id || idx} className="preview-field">
                                                    <label>
                                                        {q.question_text}
                                                        {q.is_required && <span style={{ color: 'red' }}> *</span>}
                                                    </label>
                                                    {q.question_type === 'text' && (
                                                        <input type="text" placeholder={q.placeholder || 'Enter your answer'} disabled style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', width: '100%' }} />
                                                    )}
                                                    {q.question_type === 'number' && (
                                                        <input type="number" placeholder={q.placeholder || 'Enter a number'} disabled style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', width: '100%' }} />
                                                    )}
                                                    {(q.question_type === 'multiple_choice' || q.question_type === 'dropdown') && (
                                                        <select disabled style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', width: '100%' }}>
                                                            <option>Select an option</option>
                                                            {q.options?.map((opt, i) => (
                                                                <option key={i}>{opt}</option>
                                                            ))}
                                                        </select>
                                                    )}
                                                    {q.question_type === 'checkbox' && (
                                                        <div>
                                                            {q.options?.map((opt, i) => (
                                                                <label key={i} style={{ display: 'block', marginBottom: '0.5rem' }}>
                                                                    <input type="checkbox" disabled style={{ marginRight: '0.5rem' }} />
                                                                    {opt}
                                                                </label>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {q.help_text && q.help_text.length < 180 && <small>{q.help_text}</small>}
                                                    {q.help_text && q.help_text.length >= 180 && (
                                                        <small>{q.help_text.slice(0, 160)}… (expandable on apply form)</small>
                                                    )}
                                                </div>
                                            );
                                            return (
                                                <>
                                                    {pre.filter((q) => q.question_text !== 'Cover letter (optional)').map(renderQ)}
                                                    <div className="preview-field">
                                                        <label><strong>Resume Upload *</strong></label>
                                                        <input type="file" disabled style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', width: '100%' }} />
                                                        <small>Required — after name, email, phone, city &amp; state</small>
                                                    </div>
                                                    {pre.filter((q) => q.question_text === 'Cover letter (optional)').map(renderQ)}
                                                    {(previewData?.formSettings || formSettings)?.form_show_linkedin_field && (
                                                        <div className="preview-field">
                                                            <label>LinkedIn Profile URL</label>
                                                            <input type="url" placeholder="https://linkedin.com/in/yourprofile" disabled style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', width: '100%' }} />
                                                        </div>
                                                    )}
                                                    {post.length > 0 && (
                                                        <div style={{ margin: '1rem 0 0.5rem', fontWeight: 700 }}>
                                                            After résumé — eligibility, EEO &amp; disclosures
                                                        </div>
                                                    )}
                                                    {post.map(renderQ)}
                                                </>
                                            );
                                        })()}

                                        <button 
                                            disabled 
                                            style={{
                                                backgroundColor: (previewData?.formSettings || formSettings)?.form_primary_color || '#667eea',
                                                color: 'white',
                                                padding: '0.75rem 2rem',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'not-allowed',
                                                marginTop: '1rem'
                                            }}
                                        >
                                            Submit Application
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Navigation Buttons */}
                <div className="step-navigation">
                    {currentStep > 1 && (
                        <button onClick={handleBack} className="nav-button back-button">
                            ← Back
                        </button>
                    )}
                    <div style={{ flex: 1 }} />
                    {currentStep < 4 ? (
                        <button onClick={handleNext} className="nav-button next-button">
                            Next →
                        </button>
                    ) : (
                        <button 
                            onClick={handleCreateForm} 
                            className="nav-button create-button"
                            disabled={isCreating}
                        >
                            {isCreating ? 'Creating...' : 'Create Application Form'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApplicationFormSetupPage;
